import { NoteButton } from "@components/tabsButtons/TabsButtons";
import { Form, FieldArray, useFormikContext, getIn } from "formik";
import type {
	BelongsToLuc,
	ModuleFormValues,
	ModuleProps,
	OptionalSubmodule,
	StartWithWithoutGeneric,
	T2Schema,
} from "@typedefs/modulesInterfaces";
import { FieldType } from "@typedefs/modulesInterfaces";
import { useAppNavigate, useAppSelector, useCustomTranslationHandler, usePermissions } from "@app/hooks";
import { selectCurrentBuilder, selectShowTier1 } from "@features/builder/builderSlice";
import { encodeActivityId } from "@utils/projectIdEncoder";
import { useLocation, useBlocker } from "react-router-dom";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import FormSubmodule from "@components/formSubmodule/FormSubmodule";
import AddAccordionButton from "@components/input/AddAccordionButton";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import { useContextAwareGetLandUseChange } from "@hooks/index";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	BEModulesObj,
	FEModules,
	moduleDescriptions,
} from "@utils/moduleList";
import { handleDisableSww } from "./moduleUtils";
import { preventFormSubmit } from "./inputs/utils";
import { TranslatedFormSubmodule } from "@components/formSubmodule/TranslatedFormSubmodule";
import ModuleContext from "../ModuleContext";
import TranslatableText from "@components/translations/TranslatableText";
import ModuleFooter from "./ModuleFooter";
import ConfirmNavigation from "@components/confirmNavigation/ConfirmNavigation";
import ConditionalSectionRenderer from "./ConditionalSectionRenderer";

const isSWW = (inputType: FieldType) =>
	[FieldType.SWW, FieldType.SWW_BOOL, FieldType.SWW_SELECT].includes(inputType);

const GenericModule = ({
	title,
	moduleSchema,
	closingNotes,
	t2Schema,
	children,
	beModuleType,
	isLoading,
	footerError,
	blockingError,
	disableOptionals,
	isError,
}: ModuleProps) => {
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const showTier1 = useAppSelector(selectShowTier1);
	const {
		dirty,
		values,
		setFieldValue,
		touched,
		submitForm,
		validateForm,
		isSubmitting,
		errors,
	} = useFormikContext<ModuleFormValues>();

	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const lucId = moduleList.find((module) => module.id === FEModules.LandUseChange)?.uniqueId;

	const { data: lucData } = useContextAwareGetLandUseChange(lucId ?? 0, { refetchOnMountOrArgChange: true, skip: !lucId });
	const navigate = useAppNavigate();
	const location = useLocation();
	const { isReadOnly, isPublic } = usePermissions();

	const feModuleType = BEModulesObj[beModuleType];
	const description = moduleDescriptions[feModuleType];
	const number = useMemo(() => {
		const currentModule = moduleList.find((step) => step.id === feModuleType);
		return currentModule?.uniqueId ?? "0.0";
	}, [moduleList, feModuleType]);

	const { belongsToLuc } = useContext(ModuleContext)

	// Set when the user clicks "Save and proceed". Navigation is deferred to the
	// effect below so it happens only after Formik's resetForm (called in the
	// module onSubmit on success) has committed and cleared dirty/touched —
	// otherwise useBlocker would still see stale dirty state and block the nav.
	const pendingNavRef = useRef(false);

	useEffect(() => {
		if (lucData && !getIn(values, "module.land_use_change"))
			setFieldValue("module.land_use_change", lucData.module);
	}, [lucData, setFieldValue]);

	const shouldBlock = useCallback(
		() => Object.keys(touched).length > 0 && dirty,
		[dirty, touched],
	);
	// Guards unsaved edits from here rather than from ModuleFooter: the footer is
	// hidden in the Tier 2 view, and a guard unmounting with it would let those
	// edits be navigated away silently.
	const blocker = useBlocker(isReadOnly ? false : shouldBlock);

	const handleNext = useCallback(async () => {
		const currentModulePath = location.pathname.split("/").pop();
		const currentIndex = moduleList.findIndex((step) => step.path === currentModulePath);
		const nextModulePath =
			currentIndex === moduleList.length - 1
				? "complete"
				: `${moduleList[currentIndex + 1].path}`;

		const activityIdToUse = isPublic && activeActivityId
			? (() => {
				try {
					return encodeActivityId(activeActivityId);
				} catch (error) {
					console.error('Failed to encode activity ID:', error);
					return activeActivityId.toString();
				}
			})()
			: activeActivityId;

		const basePath = `/builder/${activityIdToUse}`;
		const targetPath = `${basePath}/${nextModulePath}`;
		navigate({ path: targetPath });
	}, [location.pathname, moduleList, navigate, isPublic, activeActivityId]);

	// Validates then submits. Navigation is not done here: resetForm's clearing
	// of dirty/touched only commits on the next render, so navigating inline
	// would race the blocker. Instead we arm pendingNavRef and let the effect
	// below navigate once the save has settled.
	const handleSaveAndProceed = async () => {
		const validationErrors = await validateForm();
		if (Object.keys(validationErrors).length > 0) return;
		pendingNavRef.current = true;
		await submitForm();
	};

	// Deferred navigation for "Save and proceed": run only after the submit has
	// settled. On failure, abort (stay on the page); on success, wait until
	// resetForm has cleared dirty/touched so useBlocker won't block the nav.
	useEffect(() => {
		if (!pendingNavRef.current || isSubmitting) return;
		if (isError) {
			pendingNavRef.current = false;
			return;
		}
		if (!dirty && Object.keys(touched).length === 0) {
			pendingNavRef.current = false;
			handleNext();
		}
	}, [isSubmitting, isError, dirty, touched, handleNext]);

	const body = blockingError ? (
		<section>
			<div className="module-group">
				<div className="pos-relative mb-2 pb-1">
					<h2 className="fs-14 ff-medium-ext module-header w-fit">{title}</h2>
					<div className="module-number header-number ff-light-ext">
						{number}
					</div>
				</div>

				<span className="error fs-13 pb-2">{blockingError}</span>
			</div>
		</section>
	) : (
		<section>
			<Form onKeyDown={preventFormSubmit}>
				<div className="module-group">
					<div className="pos-relative mb-2 pb-1">
						<h2 className="fs-14 ff-medium-ext module-header w-fit">
							<TranslatableText translationKey={title} />
						</h2>
						<div className="module-number header-number ff-light-ext">
							{number}
						</div>
					</div>

					<div className="pos-relative mb-3">
						<p className="fs-14 pe-3">
							<TranslatableText {...description} />
						</p>
						<div
							className="pos-absolute d-flex align-items-center h-100"
							style={{ top: 0, left: "98%" }}
						>
							<NoteButton />
						</div>
					</div>

					{moduleSchema.initInputGroups.map((input, index) => {
						const curatedInput = handleDisableSww({ input, belongsToLuc, isReadOnly });
						return input.type === FieldType.HECTARES ? (
							/* if the module belongs to land use change we display the area from luc */
							<GenericInputGroup
								inputGroup={{
									...input,
									inputName: input.inputName,
								}}
								key={input.label}
							/>
						) : (
							<div className="py-2" key={input.label}>
								<GenericInputGroup
									inputGroup={{ ...curatedInput, disabled: isReadOnly }}
								/>
							</div>
						);
					})}

					{moduleSchema.mandatorySubmodules.map((subModule) => {
						return (
							<TranslatedFormSubmodule
								submoduleName={subModule.name}
								key={subModule.name}
								note={subModule.note}
							>
								{subModule.conditionalSections?.map((section, index) => (
									section.isAtTheStart && (
										<ConditionalSectionRenderer
											key={`${section.conditionName}-${index}`}
											conditionalSection={section}
											showConditionalSection={getIn(values, section.conditionName)}
											isReadOnly={isReadOnly}
										/>
									)
								))}

								{/* Regular input groups */}
								{subModule.inputGroups.map((input, index) => {
									const curatedInput = handleDisableSww({
										input,
										belongsToLuc,
										isReadOnly
									});

									return (
										<div className="py-2" key={input.label}>
											<GenericInputGroup
												inputGroup={{
													...curatedInput,
													disabled: isReadOnly
												}}
											/>
										</div>
									);
								})}

								{/* Conditional section at the end if applicable */}
								{subModule.conditionalSections?.map((section, index) => (
									!section.isAtTheStart && (
										<ConditionalSectionRenderer
											key={`${section.conditionName}-${index}`}
											conditionalSection={section}
											showConditionalSection={getIn(values, section.conditionName)}
											isReadOnly={isReadOnly}
										/>
									)
								))}
							</TranslatedFormSubmodule>
						);
					})}

					{!disableOptionals &&
						moduleSchema.optionalSubmodules?.map((subModule, index) => (
							<AccordionList
								subModule={subModule}
								key={subModule.name}
								t2Schema={t2Schema}
								belongsToLuc={belongsToLuc}
								isReadOnly={isReadOnly}
							/>
						))}

					{children ? children({ belongsToLuc, isSWW }) : null}
				</div>

				<ModuleFooter
					footerError={footerError}
					errors={errors}
					touched={touched}
					isLoading={isLoading || isSubmitting}
					isDisabled={isLoading || isSubmitting || isReadOnly}
					handleSaveAndProceed={handleSaveAndProceed}
					handleNext={handleNext}
					closingNotes={closingNotes}
				/>
			</Form>
		</section>
	);

	// Tier 2 hides the module form but keeps its footer: save / save-and-proceed /
	// skip are module-level actions, and reusing this same ModuleFooter is what
	// keeps them identical across the two tabs.
	const footerOnly = (
		<section className="module-footer-only">
			<Form onKeyDown={preventFormSubmit}>
				<ModuleFooter
					footerError={footerError}
					errors={errors}
					touched={touched}
					isLoading={isLoading || isSubmitting}
					isDisabled={isLoading || isSubmitting || isReadOnly}
					handleSaveAndProceed={handleSaveAndProceed}
					handleNext={handleNext}
					closingNotes={closingNotes}
				/>
			</Form>
		</section>
	);

	return (
		<>
			{!isReadOnly ? <ConfirmNavigation blocker={blocker} /> : null}
			{showTier1 ? body : blockingError ? null : footerOnly}
		</>
	);
};

const AccordionList = ({
	subModule,
	t2Schema,
	isReadOnly,
	belongsToLuc,
}: {
	subModule: OptionalSubmodule<any>;
	t2Schema?: T2Schema;
	isReadOnly: boolean;
	belongsToLuc?: BelongsToLuc;
}) => {
	const { values, setFieldValue } = useFormikContext<ModuleFormValues>();
	const subValues = getIn(values, subModule.inputName);
	const translationHandler = useCustomTranslationHandler();
	//handle push is to add a new Accordion
	const handlePush = (push: (obj: any) => void) => {
		push({ ...subModule.initialValues });
		if (subModule.linkedTierTwoInputs?.length && t2Schema) {
			for (const linkedT2 of subModule.linkedTierTwoInputs) {
				const foundSubmodule = t2Schema.optionalSubmodules?.find(
					(optSub) => optSub.inputName === linkedT2,
				);
				if (foundSubmodule?.initialValues) {
					const t2SubmoduleValues = getIn(values, linkedT2);
					setFieldValue(linkedT2, [
						...t2SubmoduleValues,
						foundSubmodule?.initialValues,
					]);
				}
			}
		}
	};
	const handleRemove = (
		remove: <T>(index: number) => T | undefined,
		index: number,
	) => {
		remove(index);
		if (subModule.linkedTierTwoInputs?.length && t2Schema) {
			for (const linkedT2 of subModule.linkedTierTwoInputs) {
				// getIn returns a reference to the live Formik array — copy before filtering
				// to avoid mutating internal Formik state in place.
				const t2SubmoduleValues: unknown[] = getIn(values, linkedT2);
				setFieldValue(linkedT2, t2SubmoduleValues.filter((_, i) => i !== index));
			}
		}
	};

	return Array.isArray(subValues) ? (
		<FieldArray name={subModule.inputName}>
			{({ remove, push }) => {
				const translatedSubModuleName = translationHandler(subModule.name);
				return (
					<>
						{subValues.length > 0 &&
							subValues.map((element, index) => (
								<FormSubmodule
									key={`${subModule.inputName}-${index}`}
									submoduleName={`${index + 1}. ${subModule.name}`}
									removeHandler={() => handleRemove(remove, index)}
									initiallyOpen
								>
									{/* Conditional sections at the start if applicable */}
									{subModule.conditionalSections ? (
										subModule.conditionalSections.map((section) => (
											section.isAtTheStart ? (
												<ConditionalSectionRenderer
													key={`${section.conditionName}-${index}`}
													conditionalSection={section}
													showConditionalSection={getIn(element, section.conditionName)}
													isReadOnly={isReadOnly}
													index={index}
												/>
											) : null
										))
									) : null}

									{subModule.inputGroups.map((input, i) => {
										const curatedInput = handleDisableSww({ input, belongsToLuc, isReadOnly, index });
										return (
											<div className="py-2" key={`${input.label}-${index}-${i}`}>
												<GenericInputGroup inputGroup={{ ...curatedInput, disabled: isReadOnly }}
												/>
											</div>
										);
									})}

									{/* Conditional sections at the end if applicable */}
									{subModule.conditionalSections ? (
										subModule.conditionalSections.map((section) => (
											!section.isAtTheStart ? (
												<ConditionalSectionRenderer
													key={`${section.conditionName}-${index}`}
													conditionalSection={section}
													showConditionalSection={getIn(element, section.conditionName)}
													isReadOnly={isReadOnly}
													index={index}
												/>
											) : null
										))
									) : null}
								</FormSubmodule>
							))}

						{subModule.repeatable ? (
							<AddAccordionButton
								label={`Add ${translatedSubModuleName}`}
								clickHandler={() => handlePush(push)}
								disabled={subModule?.max ? subValues.length >= subModule?.max : false}
							/>
						) : null}
					</>
				)
			}}
		</FieldArray>
	) : (
		<TranslatedFormSubmodule
			submoduleName={subModule.name}
			note={subModule.note}
			initiallyOpen
		>
			{subModule.inputGroups.map((input, i) => {
				const swwInput = input as StartWithWithoutGeneric;
				return isSWW(input.type) ? (
					<div className="py-2" key={`${input.label}-${i}`}>
						<GenericInputGroup inputGroup={handleDisableSww({ input: swwInput, isReadOnly, belongsToLuc }) as StartWithWithoutGeneric} />
					</div>
				) : (
					<div className="py-2" key={`${input.label}-${i}`}>
						<GenericInputGroup inputGroup={{ ...input, disabled: isReadOnly }} />
					</div>
				);
			})}
		</TranslatedFormSubmodule>
	);
};

export default GenericModule;
